import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { ProfileService } from '../../entities/Profile';
import { AudioSilenceAlert } from '../AudioSilenceAlert';
import { ChromeRequiredToast } from '../ChromeRequiredToast/ChromeRequiredToast';
import TranscriptionServerStatus from '../TranscriptionServerStatus';
import { WebphoneButton } from '../Webphone/WebphoneButton';
import { WebphonePopover } from '../Webphone/WebphonePopover';
import { NoiseBackground } from '@/components/ui/noise-background';
import { CenterOnboardingGate } from '@/features/onboarding';
import { useChromeRequiredGate } from '../../hooks/useChromeRequiredGate';
import { useGoServerStore } from '../../stores/goServerStore';
import { useProfileSettingsStore } from '../../stores/profileSettingsStore';
import { useChecklistStore } from '../../stores/checklistStore';
import supabase from '../../utils/supabase';

import {
  CogIcon,
  ArrowRightStartOnRectangleIcon,
  PhoneArrowDownLeftIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  ListBulletIcon,
  CpuChipIcon,
  BookOpenIcon,
  Bars3Icon,
  RocketLaunchIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  FunnelIcon,
  PresentationChartLineIcon
} from '@heroicons/react/24/outline';
// Layout no longer relies on children prop; routes render via <Outlet />

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, signOut, role, centerId, loading: authLoading } = useAuth();
  const { salesCallStatus, setCallMode, setSimulatorDifficulty, setSimulatorVoice, startSalesCall, tourActiveStepId, spotlightTourActive } = useGoServerStore();
  const { showChromeToast, dismissChromeToast } = useChromeRequiredGate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [simulatorModalType, setSimulatorModalType] = useState<'enable' | 'disable' | null>(null);
  const [isOnboardingSimulatorModal, setIsOnboardingSimulatorModal] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [hasLeadAssignments, setHasLeadAssignments] = useState(false);

  // Fetch user profile for additional info and logging
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        setUserProfile(null);
        setProfileLoaded(true); // Mark as loaded even if no user
        return;
      }

      try {
        const profileService = new ProfileService();
        const profile = await profileService.getByUserId(user.id);
        setUserProfile(profile);
        setProfileLoaded(true); // Mark profile as loaded

        // Comprehensive logging for user type and profile
        console.log('=== USER AUTHENTICATION INFO ===');
        console.log('User ID:', user.id);
        console.log('User Email:', user.email);
        console.log('Role from JWT (AuthContext):', role);
        console.log('Center ID from JWT (AuthContext):', centerId);
        console.log('Profile from Database:', profile);
        console.log('Profile Role:', profile?.role);
        console.log('Profile Center ID:', profile?.center_id);
        console.log('Auth Loading:', authLoading);
        console.log('================================');
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setUserProfile(null);
        setProfileLoaded(true); // Mark as loaded even on error
      }
    };

    fetchProfile();
  }, [user?.id, role, centerId]);

  // Log role changes
  useEffect(() => {
    if (role) {
      console.log('[Layout] Role changed:', role);
      console.log('[Layout] Center ID:', centerId);
    }
  }, [role, centerId]);

  // Check if representative has any lead assignments (to show Leads nav link)
  useEffect(() => {
    const effectiveRole = userProfile?.role || role;
    if (!effectiveRole) return;
    if (effectiveRole !== 'representative') {
      setHasLeadAssignments(true);
      return;
    }
    if (!user?.id) return;
    supabase
      .from('crm_lead_assignments')
      .select('lead_id', { count: 'exact', head: true })
      .eq('rep_user_id', user.id)
      .then(r => setHasLeadAssignments((r.count ?? 0) > 0));
  }, [user?.id, userProfile?.role, role]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleOpenSimulatorEnable = () => {
      setSidebarOpen(false);
      if (location.pathname !== '/') {
        navigate('/');
      }
      setIsOnboardingSimulatorModal(true);
      setSimulatorModalType('enable');
    };
    window.addEventListener('open-simulator-enable-modal', handleOpenSimulatorEnable);
    return () => window.removeEventListener('open-simulator-enable-modal', handleOpenSimulatorEnable);
  }, [location.pathname, navigate]);

  // Build navigation based on role - use useMemo to make it reactive
  // Representatives should NOT see any management pages
  // Management pages are hidden by default and only shown after profile role is confirmed
  const navigation = useMemo(() => {
    const baseNavigation = [
      // Dashboard link removed as requested, accessing via Logo
      { name: t('navigation.calls'), href: '/calls', icon: PhoneArrowDownLeftIcon },
      // Cost Analysis moved to admin-only section
      { name: t('navigation.downloads'), href: '/settings?tab=downloads', icon: ArrowDownTrayIcon },
      { name: t('navigation.guides'), href: 'https://midas.ravpage.co.il/Closer-guide', icon: BookOpenIcon },
    ];

    // If auth is still loading, show base navigation only
    if (authLoading) {
      console.log('[Layout] Auth loading - showing base navigation only');
      return baseNavigation;
    }

    // Use profile role as source of truth (most accurate) if available
    // Fall back to JWT role if profile is not loaded yet or doesn't have a role
    const effectiveRole = userProfile?.role || role;

    // If we don't have a role yet, show base navigation only
    // This allows navigation to appear even if profile loading fails
    if (!effectiveRole) {
      console.log('[Layout] No role found (profile loaded:', profileLoaded, 'profile role:', userProfile?.role, 'JWT role:', role, ') - showing base navigation only');
      return baseNavigation;
    }

    // Explicitly block representatives from seeing any admin pages
    if (effectiveRole === 'representative') {
      console.log('[Layout] Representative role detected (from profile:', userProfile?.role, 'or JWT:', role, ') - no admin pages will be shown');
      if (hasLeadAssignments) {
        baseNavigation.push({ name: t('navigation.leads'), href: '/leads', icon: FunnelIcon });
      }
      return baseNavigation;
    }

    // Only add management pages for authorized roles
    // Representatives are explicitly excluded above
    if (effectiveRole === 'super_admin') {
      baseNavigation.push(
        { name: t('navigation.leads'), href: '/leads', icon: FunnelIcon },
        { name: t('navigation.centerManagement'), href: '/admin/centers', icon: BuildingOfficeIcon },
        { name: t('navigation.userManagement'), href: '/admin/users', icon: UsersIcon },
        { name: t('agentConfig.title'), href: '/admin/agent-config', icon: CpuChipIcon },
        { name: t('navigation.representativeStats'), href: '/admin/representative-stats', icon: ChartBarIcon },
        { name: t('navigation.logs'), href: '/admin/logs', icon: ListBulletIcon },
        { name: t('navigation.crmActivity'), href: '/admin/crm-activity', icon: PresentationChartLineIcon }
      );
    } else if (effectiveRole === 'center_manager') {
      baseNavigation.push(
        { name: t('navigation.leads'), href: '/leads', icon: FunnelIcon },
        { name: t('navigation.userManagement'), href: '/admin/users', icon: UsersIcon },
        { name: t('navigation.representativeStats'), href: '/admin/representative-stats', icon: ChartBarIcon },
      );
    }
    // Representatives (role === 'representative') get no additional pages - handled by early return above

    console.log('[Layout] Navigation updated for role:', effectiveRole, '(profile:', userProfile?.role, 'JWT:', role, ') Navigation items:', baseNavigation.length);
    return baseNavigation;
  }, [role, userProfile?.role, profileLoaded, authLoading, t, hasLeadAssignments]);


  // Set initial document direction
  useEffect(() => {
    const isRTL = i18n.language === 'he' || i18n.language === 'ar';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);


  // Check if current language is RTL
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';

  const isSimulatorTourStep = tourActiveStepId === 'live-setup-simulator';
  const isOnboardingTourActive = spotlightTourActive;

  const openSimulatorModal = () => {
    setSidebarOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
    }
    setSimulatorModalType(salesCallStatus.callMode === 'simulator' ? 'disable' : 'enable');
  };

  return (
    <div className="h-screen bg-neutral-50 dark:bg-neutral-900 overflow-hidden font-inter text-base leading-normal">
      <div className="flex h-full">
        {/* Sidebar - Always collapsed */}
        <div
          className={`fixed inset-y-0 z-50 bg-white dark:bg-neutral-800 transition-all duration-300  w-16 sm:w-20 lg:w-[5vw] ${isRTL
            ? `border-l border-neutral-200 dark:border-neutral-700  ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`
            : `border-r border-neutral-200 dark:border-neutral-700  rounded-l-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`
            } ${isSimulatorTourStep ? 'pointer-events-none grayscale opacity-40' : ''} ${isOnboardingTourActive ? 'pointer-events-none' : ''}`}
          style={{
            boxShadow: 'rgba(50, 50, 105, 0.15) 0px 2px 5px 0px, rgba(0, 0, 0, 0.05) 0px 1px 1px 0px',
            [isRTL ? 'right' : 'left']: 0
          }}
        >
          <div className="flex h-full flex-col sidebar-content">
            {/* Header with Logo */}
            <div className={`flex h-16 items-center px-4 justify-center`}>
              <Link
                to="/"
                onClick={() => setSidebarOpen(false)}
                title={t('navigation.dashboard')}
                className={`flex items-center justify-center p-1.5 rounded-full transition-all duration-300 
                  ${location.pathname === '/'
                    ? 'scale-[1.15]'
                    : 'hover:bg-neutral-100 active:bg-neutral-200 dark:hover:bg-neutral-700 dark:active:bg-neutral-600'
                  }`}
              >
                <span className="inline-flex h-6 overflow-hidden lg:overflow-visible">
                  <img
                    src="/logoSmall.png"
                    alt="Logo"
                    className="h-7 w-auto -translate-y-0.5 lg:h-6 lg:translate-y-0"
                  />
                </span>
              </Link>
            </div>

            {/* Webphone toggle */}
            <div className="flex items-center justify-center pb-2">
              <WebphoneButton />
            </div>

            {/* Navigation */}
            <nav className={`flex flex-1 min-h-0 flex-col overflow-y-auto py-2 px-2 text-xs text-start`} dir={isRTL ? 'rtl' : 'ltr'}>
              <div className="space-y-1">
                {navigation.map((item) => {
                  const isActive = item.href.includes('?')
                    ? (location.pathname + location.search) === item.href
                    : location.pathname === item.href;

                  const isExternal = item.href.startsWith('http');
                  const commonClasses = `flex gap-2 rtl:justify-end transition-all duration-200 group relative ${isActive
                    ? 'bg-brand-500 text-white shadow-sm'
                    : 'text-text-700 hover:bg-neutral-100 hover:text-text-900 dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-neutral-100'
                    } flex-col items-center px-1 py-2 rounded-lg text-xs font-medium leading-tight`;

                  const content = (
                    !isRTL ? (
                      <>
                        <item.icon className={`h-5 w-5 mb-1`} />
                        <span className={`text-xs font-medium text-center leading-tight hidden xl:block`}>
                          {item.name}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className={`text-xs font-medium text-center leading-tight hidden xl:block`}>
                          {item.name}
                        </span>
                        <item.icon className={`h-5 w-5 mb-1`} />
                      </>
                    )
                  );

                  if (isExternal) {
                    return (
                      <a
                        key={item.name}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setSidebarOpen(false)}
                        className={commonClasses}
                        title={item.name}
                      >
                        {content}
                      </a>
                    );
                  }

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={commonClasses}
                      title={item.name}
                    >
                      {content}
                    </Link>
                  );
                })}
              </div>
            </nav>

            {/* User Info and Logout */}
            <div className="px-4 py-4 border-t  border-neutral-200 dark:border-neutral-700">
              <div className="flex flex-col items-center space-y-2">
                <button
                  type="button"
                  onClick={openSimulatorModal}
                  data-tour-id="layout-simulator"
                  className={`relative w-full min-h-[56px] rounded-lg text-white transition-all duration-300 overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900 ${
                    salesCallStatus.callMode === 'simulator'
                      ? 'shadow-lg shadow-emerald-500/30 ring-2 ring-emerald-300/60 dark:ring-emerald-500/40'
                      : 'shadow-lg hover:scale-[1.01] hover:shadow-orange-500/30'
                  }`}
                  title={t('simulator.mode.simulator')}
                >
                  <NoiseBackground
                    containerClassName={`w-full h-full min-h-[56px] rounded-lg ${
                      salesCallStatus.callMode === 'simulator' ? 'bg-emerald-600' : 'bg-brand-500'
                    }`}
                    className="flex flex-col items-center justify-center gap-1 py-2 px-2 h-full w-full text-white"
                    gradientColors={
                      salesCallStatus.callMode === 'simulator'
                        ? ['#064e3b', '#059669', '#6ee7b7']
                        : ['#8a2e1b', '#F25C3D', '#fcc8c0']
                    }
                    noiseIntensity={0.2}
                    speed={0.25}
                  >
                    <RocketLaunchIcon className="h-5 w-5 drop-shadow-sm" />
                    <span className="text-[10px] font-semibold leading-none text-center drop-shadow-sm">
                      {t('simulator.mode.simulator')}
                    </span>
                  </NoiseBackground>
                </button>
                <Link
                  to="/settings?tab=customization"
                  onClick={() => setSidebarOpen(false)}
                  className="w-full p-2 rounded-lg transition-all duration-200 text-neutral-600 hover:text-brand-600 dark:text-neutral-400 dark:hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900"
                  title={t('settings.tabs.customization')}
                >
                  <div className="flex flex-col items-center gap-1">
                    <CogIcon className="h-5 w-5" />
                    <span className="text-[10px] font-semibold leading-none text-center">
                      {t('settings.tabs.customization')}
                    </span>
                  </div>
                </Link>
                <button
                  onClick={async () => {
                    if (signingOut) return;
                    setSigningOut(true)
                    try {
                      await signOut()
                    } catch (error) {
                      console.error('Failed to sign out:', error)
                    } finally {
                      setSigningOut(false)
                    }
                  }}
                  disabled={signingOut}
                  className={`p-2 rounded-lg transition-all duration-200 ${signingOut ? 'opacity-60 cursor-not-allowed' : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-700'}`}
                  title={t('auth.signOutTooltip')}
                >
                  {signingOut ? (
                    <span className="inline-block h-5 w-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Removed Software Versions from navbar; now shown in Settings page */}
          </div>
        </div>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-neutral-600 bg-opacity-75 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {simulatorModalType && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 z-0 bg-black/55 backdrop-blur-[2px]"
              onClick={isOnboardingSimulatorModal ? undefined : () => setSimulatorModalType(null)}
              aria-hidden="true"
            />
            <div className="relative z-10 w-full max-w-2xl overflow-hidden rounded-2xl border border-neutral-200 bg-white p-0 shadow-2xl dark:border-neutral-700 dark:bg-neutral-800" dir={isRTL ? 'rtl' : 'ltr'}>
              <div className="relative border-b border-neutral-200 bg-gradient-to-r from-brand-600 to-brand-500 px-5 py-4 text-white dark:border-neutral-700">
                <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-white/15 blur-xl" />
                <div className="pointer-events-none absolute -bottom-8 left-6 h-20 w-20 rounded-full bg-white/10 blur-lg" />
                <div className="relative z-0 mb-4 flex items-start gap-3 pe-12">
                  <div>
                    <h2 className="text-lg font-semibold text-white">
                      {simulatorModalType === 'enable' ? t('simulator.modal.enableTitle') : t('simulator.modal.disableTitle')}
                    </h2>
                    <p className="mt-1 text-sm text-white/90">
                      {simulatorModalType === 'enable' ? t('simulator.modal.enableDescription') : t('simulator.modal.disableDescription')}
                    </p>
                  </div>
                </div>
                {!isOnboardingSimulatorModal && (
                  <button
                    type="button"
                    onClick={() => setSimulatorModalType(null)}
                    className={`absolute top-4 z-20 rounded-lg p-1.5 text-white/85 hover:bg-white/20 hover:text-white ${isRTL ? 'left-4' : 'right-4'}`}
                    title={t('simulator.modal.close')}
                    aria-label={t('simulator.modal.close')}
                  >
                    <XMarkIcon className="h-5 w-5 shrink-0 pointer-events-none" />
                  </button>
                )}
              </div>

              <div className="space-y-4 p-5">
                {simulatorModalType === 'enable' && (
                  <>
                    <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/30">
                      <div className={`flex items-start gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                        <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                            {t('simulator.modal.headphonesWarningTitle')}
                          </p>
                          <p className="mt-1 text-xs text-amber-700 dark:text-amber-200">
                            {t('simulator.modal.headphonesWarningDescription')}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-neutral-200 bg-white p-3 dark:border-neutral-700 dark:bg-neutral-900">
                      <p className="mb-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                        {t('simulator.modal.difficultyTitle')}
                      </p>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                        <button
                          type="button"
                          disabled={salesCallStatus.isActive}
                          onClick={() => setSimulatorDifficulty('easy')}
                          className={`rounded-lg border px-3 py-2 text-start transition-all ${
                            salesCallStatus.simulatorDifficulty === 'easy'
                              ? 'border-emerald-500 bg-emerald-100 ring-2 ring-emerald-300 dark:border-emerald-400 dark:bg-emerald-900/40 dark:ring-emerald-700'
                              : 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30'
                          } ${salesCallStatus.isActive ? 'cursor-not-allowed opacity-60' : 'hover:scale-[1.01]'}`}
                        >
                          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                            {t('simulator.modal.difficulty.easy.title')}
                          </p>
                          <p className="mt-1 text-xs text-emerald-800 dark:text-emerald-200">
                            {t('simulator.modal.difficulty.easy.description')}
                          </p>
                        </button>
                        <button
                          type="button"
                          disabled={salesCallStatus.isActive}
                          onClick={() => setSimulatorDifficulty('medium')}
                          className={`rounded-lg border px-3 py-2 text-start transition-all ${
                            salesCallStatus.simulatorDifficulty === 'medium'
                              ? 'border-amber-500 bg-amber-100 ring-2 ring-amber-300 dark:border-amber-400 dark:bg-amber-900/40 dark:ring-amber-700'
                              : 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30'
                          } ${salesCallStatus.isActive ? 'cursor-not-allowed opacity-60' : 'hover:scale-[1.01]'}`}
                        >
                          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                            {t('simulator.modal.difficulty.medium.title')}
                          </p>
                          <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                            {t('simulator.modal.difficulty.medium.description')}
                          </p>
                        </button>
                        <button
                          type="button"
                          disabled={salesCallStatus.isActive}
                          onClick={() => setSimulatorDifficulty('hard')}
                          className={`rounded-lg border px-3 py-2 text-start transition-all ${
                            salesCallStatus.simulatorDifficulty === 'hard'
                              ? 'border-rose-500 bg-rose-100 ring-2 ring-rose-300 dark:border-rose-400 dark:bg-rose-900/40 dark:ring-rose-700'
                              : 'border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/30'
                          } ${salesCallStatus.isActive ? 'cursor-not-allowed opacity-60' : 'hover:scale-[1.01]'}`}
                        >
                          <p className="text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">
                            {t('simulator.modal.difficulty.hard.title')}
                          </p>
                          <p className="mt-1 text-xs text-rose-800 dark:text-rose-200">
                            {t('simulator.modal.difficulty.hard.description')}
                          </p>
                        </button>
                      </div>
                    </div>

                    <div className="rounded-xl border border-neutral-200 bg-white p-3 dark:border-neutral-700 dark:bg-neutral-900">
                      <p className="mb-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                        {t('simulator.modal.voiceTitle')}
                      </p>
                      <p className="mb-3 text-xs text-neutral-600 dark:text-neutral-400">
                        {t('simulator.modal.voiceHint')}
                      </p>
                      <select
                        value={salesCallStatus.simulatorVoice ?? 'Rex'}
                        onChange={(e) => setSimulatorVoice(e.target.value as 'Eve' | 'Ara' | 'Rex' | 'Sal' | 'Leo')}
                        disabled={salesCallStatus.isActive}
                        className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-900 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 disabled:opacity-60"
                      >
                        <option value="Eve">{t('simulator.modal.voice.eve')}</option>
                        <option value="Ara">{t('simulator.modal.voice.ara')}</option>
                        <option value="Rex">{t('simulator.modal.voice.rex')}</option>
                        <option value="Sal">{t('simulator.modal.voice.sal')}</option>
                        <option value="Leo">{t('simulator.modal.voice.leo')}</option>
                      </select>
                    </div>
                  </>
                )}

                {salesCallStatus.isActive && (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
                    {t('simulator.modal.activeCallWarning')}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2 border-t border-neutral-200 bg-neutral-50 px-5 py-4 dark:border-neutral-700 dark:bg-neutral-900/60">
                {!isOnboardingSimulatorModal && (
                  <button
                    type="button"
                    onClick={() => setSimulatorModalType(null)}
                    className="rounded-xl border border-neutral-200 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-700"
                  >
                    {t('simulator.modal.cancel')}
                  </button>
                )}
                {simulatorModalType === 'enable' ? (
                  <button
                    type="button"
                    disabled={salesCallStatus.isActive}
                    onClick={() => {
                      const { defaultDeviceIdSalesman, defaultDeviceIdClient } = useProfileSettingsStore.getState();
                      const { centerType } = useChecklistStore.getState();
                      setCallMode('simulator');
                      setIsOnboardingSimulatorModal(false);
                      setSimulatorModalType(null);
                      startSalesCall(defaultDeviceIdSalesman, defaultDeviceIdClient, user?.id, centerType);
                    }}
                    className={`group inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white transition-all duration-300 ${
                      salesCallStatus.isActive
                        ? 'cursor-not-allowed bg-neutral-400'
                        : 'bg-green-600 hover:bg-green-700 shadow-lg hover:shadow-xl hover:scale-105'
                    }`}
                  >
                    <PlayIcon className="h-4 w-4 flex-shrink-0" />
                    {t('dashboard.startCallButton.startCall')}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={salesCallStatus.isActive}
                    onClick={() => {
                      setCallMode('real');
                      setSimulatorModalType(null);
                    }}
                    className={`rounded-xl px-4 py-2 text-sm font-semibold text-white ${
                      salesCallStatus.isActive
                        ? 'cursor-not-allowed bg-neutral-400'
                        : 'bg-brand-600 hover:bg-brand-700'
                    }`}
                  >
                    {t('simulator.modal.exitSimulator')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className={`flex-1 transition-all duration-300 flex flex-col h-full ${isRTL
          ? 'lg:mr-[5vw]'
          : 'lg:ml-[5vw]'
          }`}>
          {location.pathname === '/' && (
            <div className="lg:hidden sticky top-0 z-30 border-b border-neutral-200 dark:border-neutral-800 bg-white/95 dark:bg-neutral-900/95 backdrop-blur px-3 py-2">
              <div className={`flex items-center gap-2 ${isRTL ? 'justify-start' : 'justify-end'}`}>
                <button
                  type="button"
                  onClick={() => setSidebarOpen((prev) => !prev)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-neutral-200 bg-white text-text-800 shadow-sm transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-700"
                  title={t('navigation.openMenu')}
                  aria-label={t('navigation.openMenu')}
                  aria-expanded={sidebarOpen}
                >
                  <Bars3Icon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Page content */}
          <main className="bg-neutral-50 dark:bg-neutral-900 flex-1 overflow-auto h-full text-base leading-normal relative">
            <Outlet />
          </main>
          <AudioSilenceAlert />

        </div>
      </div>
      <ChromeRequiredToast isOpen={showChromeToast} onDismiss={dismissChromeToast} />
      <CenterOnboardingGate />
      <TranscriptionServerStatus />
      <WebphonePopover />
    </div>
  );
};

export default Layout;
